import type { Recording } from '@/lib/protobuf/gen/recordings/recordings_pb';

/**
 * Produces a short, single-line preview of a recording's transcript for the
 * library card.
 *
 * Segments are the reliable source — every ASR path emits per-turn segments,
 * and `RecordingPlayer` renders only `transcript.segments`. The flat
 * `transcript.text` field is populated inconsistently across ASR models, so we
 * join the leading segment texts first and fall back to `text` only when there
 * are no segments. The result is whitespace-normalized and hard-capped at
 * `maxChars` on a word boundary so it never breaks the card layout (ASR text is
 * bounded input, but we cap defensively regardless).
 *
 * Returns `''` when there is no transcript or no usable text — callers should
 * treat the empty string as "no preview" and render their status/empty branch.
 */
export function transcriptSnippet(rec: Recording, maxChars = 160): string {
  const t = rec.transcript;
  if (!t) return '';

  const fromSegments = t.segments
    .map((s) => s.text)
    .filter((text) => text.trim().length > 0)
    .join(' ');
  const raw = fromSegments || t.text || '';

  const normalized = raw.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxChars) return normalized;

  // Cap at maxChars, then back up to the last space so we don't slice a word.
  const hard = normalized.slice(0, maxChars);
  const lastSpace = hard.lastIndexOf(' ');
  const clipped = lastSpace > maxChars * 0.6 ? hard.slice(0, lastSpace) : hard;
  return `${clipped.trimEnd()}…`;
}
